import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { resolveSatDownloadFailure } from "@/lib/satDownloadErrors";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CalendarRange,
  CheckCircle2,
  CloudDownload,
  ExternalLink,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Download,
  AlertTriangle,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useSatUiDiagnose } from "@/lib/useSatUiDiagnose";
import DateFieldMx from "@/components/DateFieldMx";
import {
  SatDownloadPanelIntro,
  SatDownloadPanelStack,
  SatDownloadSectionCard,
} from "@/components/sat/SatDownloadSectionCard";
import { formatIsoDateMx, monthIsoRange, todayIsoMx } from "@/lib/dateMx";
import { useSatDownloadJobsOptional } from "@/lib/satDownloadJobs";

const MESES_CORTO = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
/** Alineado con VF_SAT_MANUAL_MAX_PERIOD_DAYS (backend). */
const MANUAL_MAX_PERIOD_DAYS = 90;

function inclusiveSpanDays(startIso, endIso) {
  if (!startIso || !endIso) return 0;
  const a = new Date(`${startIso.slice(0, 10)}T12:00:00`);
  const b = new Date(`${endIso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return 0;
  return Math.round((b - a) / 86400000) + 1;
}

const RESUMEN_TIPOS = {
  ingresos: "Ingresos",
  egresos: "Egresos",
  nominas: "Nóminas",
  pagos: "Pagos",
  traslados: "Traslados",
};

function formatTsMx(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(String(iso).replace("Z", "+00:00"));
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16).replace("T", " ");
    return d.toLocaleString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(iso).slice(0, 16);
  }
}

async function downloadZipBlob(path, params, fallbackFilename) {
  const { data, headers } = await api.get(path, { responseType: "blob", params });
  if (!(data instanceof Blob) || data.size === 0) throw new Error("Archivo vacío");
  const cd = headers["content-disposition"] || headers["Content-Disposition"] || "";
  let name = fallbackFilename;
  const quoted = cd.match(/filename="([^"]+)"/i);
  if (quoted) name = quoted[1];
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 120000);
  return name;
}

/**
 * Módulo SAT seguro: consulta FIEL centralizada + descarga por rango de fechas + bitácora.
 * La carga de certificados vive en Clientes → Certificados y FIEL.
 */
export default function SatSecureDownloadPanel({
  clientId,
  dataLoadBusy,
  onStatsReload,
}) {
  const [secureFielStatus, setSecureFielStatus] = useState(null);
  const [secureFielLoading, setSecureFielLoading] = useState(false);
  const [autoDlStart, setAutoDlStart] = useState(() => {
    const today = todayIsoMx();
    return `${today.slice(0, 8)}01`;
  });
  const [autoDlEnd, setAutoDlEnd] = useState(() => todayIsoMx());
  const [autoDlBusy, setAutoDlBusy] = useState(false);
  const [autoDlResult, setAutoDlResult] = useState(null);
  const [dlProgress, setDlProgress] = useState(null);
  const [matriz, setMatriz] = useState(null);
  const [matrizError, setMatrizError] = useState(null);
  const [matrizBusy, setMatrizBusy] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodResumen, setPeriodResumen] = useState(null);
  const [periodBusy, setPeriodBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [descargasHistory, setDescargasHistory] = useState([]);
  const [procesoBitacora, setProcesoBitacora] = useState(null);
  const [satConnectionNotice, setSatConnectionNotice] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const [releaseSlotBusy, setReleaseSlotBusy] = useState(false);
  const [trackedJobId, setTrackedJobId] = useState(null);
  const satJobs = useSatDownloadJobsOptional();
  const trackedJob = trackedJobId ? satJobs?.getJob?.(trackedJobId) : null;
  const { enabled: diagnoseEnabled } = useSatUiDiagnose();

  const panelBusy = dataLoadBusy;
  useEffect(() => {
    if (!trackedJob) return;
    if (trackedJob.progress) setDlProgress(trackedJob.progress);
    if (trackedJob.status === "done" && trackedJob.result) {
      setAutoDlResult(trackedJob.result);
    }
  }, [trackedJob]);

  const activeSatJob = preflight?.active_job || null;
  const hasActiveSatPipeline = Boolean(
    activeSatJob?.job_id ||
      preflight?.download_coalesce?.should_coalesce_new_request ||
      trackedJobId,
  );

  const attachToSatJob = useCallback(
    (jobId, meta = {}) => {
      if (!jobId || !clientId) return;
      const fechaInicio = meta.fecha_inicio || autoDlStart || "";
      const fechaFin = meta.fecha_fin || autoDlEnd || "";
      const progress = meta.progress || {
        step: "running",
        message: "Descarga SAT en proceso…",
        percent: 5,
      };
      setTrackedJobId(jobId);
      setAutoDlBusy(true);
      setDlProgress(progress);
      satJobs?.registerJob?.({
        jobId,
        clientId,
        fechaInicio,
        fechaFin,
        authLabel: "FIEL",
        onTerminal: ({ status, job, failure }) => {
          if (status === "done") {
            onStatsReload?.();
            loadMatriz();
            loadDescargasHistory();
            loadProcesoBitacora();
            loadPreflight();
            const report = job?.result?.completion_report;
            if (report?.outcome === "partial") {
              toast.info(report?.interruption_note || "Descarga SAT en proceso en segundo plano.");
            } else {
              toast.success(job?.result?.message || "Descarga completada");
            }
            setAutoDlBusy(false);
          } else if (failure?.showSatAlert) {
            setSatConnectionNotice({
              title: failure.title,
              message: failure.message,
              detail: failure.detail || failure.technicalSummary || job?.error?.message,
            });
            setAutoDlBusy(false);
          }
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientId, autoDlStart, autoDlEnd, satJobs],
  );

  const showVirtualSatProgress = useCallback((virtualProgress, message) => {
    setAutoDlBusy(true);
    setDlProgress(
      virtualProgress || {
        step: "verify",
        message: message || "El SAT está procesando la solicitud anterior…",
        percent: 12,
      },
    );
    if (message) toast.info(message);
  }, []);

  const manualPeriodDays = inclusiveSpanDays(autoDlStart, autoDlEnd);
  const manualPeriodTooLong = manualPeriodDays > MANUAL_MAX_PERIOD_DAYS;
  const globalAtCapacity = Boolean(preflight?.global_sat_capacity?.at_capacity);
  const satCoolingOff = Boolean(
    preflight?.sat_cooling_off_until ||
      preflight?.checks?.sat_rate_limit?.code === "SAT_COOLING_OFF",
  );
  const downloadInProgressBlocked = Boolean(
    (preflight?.blockers || []).some((b) => b.code === "SAT_DOWNLOAD_IN_PROGRESS"),
  );
  const statusLine = trackedJob?.statusLine || preflight?.status_line || null;
  const syncAutoActive = Boolean(preflight?.modulo_descarga_activo);
  const suggestAutoSync = manualPeriodDays > 60 && !syncAutoActive && !manualPeriodTooLong;

  const reloadSecureFielStatus = async () => {
    if (!clientId) return;
    setSecureFielLoading(true);
    try {
      const { data } = await api.get("/sat/credenciales-fiel", { params: { client_id: clientId } });
      setSecureFielStatus(data);
    } catch {
      setSecureFielStatus(null);
    } finally {
      setSecureFielLoading(false);
    }
  };

  const onReleaseStuckDownload = async () => {
    if (!clientId) return;
    setReleaseSlotBusy(true);
    try {
      const { data } = await api.post("/sat/descarga-automatica/liberar-slot", null, {
        params: { client_id: clientId },
        timeout: 30000,
      });
      if (data?.client_active_after) {
        toast.warning(
          data.message ||
            "Aún hay actividad de descarga registrada. Si acaba de liberar, espere unos segundos y reintente.",
        );
      } else {
        const released = Number(data?.mongo_jobs_released || 0);
        toast.success(
          released > 0
            ? `Descarga trabada liberada (${released} job(s)). Ya puede iniciar una nueva descarga.`
            : "Slot liberado. Ya puede iniciar una nueva descarga.",
        );
        setDlProgress(null);
        setAutoDlResult(null);
        await loadPreflight();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo liberar la descarga trabada");
    } finally {
      setReleaseSlotBusy(false);
    }
  };

  const loadPreflight = async () => {
    if (!clientId) {
      setPreflight(null);
      return;
    }
    setPreflightBusy(true);
    try {
      const { data } = await api.get("/sat/descarga-preflight", {
        params: { client_id: clientId, auth_mode: "fiel" },
        timeout: 20000,
      });
      setPreflight(data);
    } catch (e) {
      setPreflight({
        ready: false,
        blockers: [{ code: "PREFLIGHT_ERROR", message: formatApiError(e.response?.data?.detail) || "No se pudo verificar pre-flight." }],
      });
    } finally {
      setPreflightBusy(false);
    }
  };

  const loadMatriz = async () => {
    if (!clientId) return;
    setMatrizBusy(true);
    setMatrizError(null);
    try {
      const { data } = await api.get("/sat/resumen-matriz", { params: { client_id: clientId } });
      setMatriz(data);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "No se pudo cargar la matriz de periodos";
      setMatriz(null);
      setMatrizError(msg);
      toast.error(msg);
    } finally {
      setMatrizBusy(false);
    }
  };

  const loadDescargasHistory = async () => {
    if (!clientId) return;
    try {
      const { data } = await api.get("/sat/descargas-control", { params: { client_id: clientId, limit: 20 } });
      setDescargasHistory(data.items || []);
    } catch {
      setDescargasHistory([]);
    }
  };

  const loadProcesoBitacora = async () => {
    if (!clientId) return;
    try {
      const { data } = await api.get("/sat/proceso-bitacora", { params: { client_id: clientId, limit: 15 } });
      setProcesoBitacora(data);
    } catch {
      setProcesoBitacora(null);
    }
  };

  const formatCallCounts = (counts) => {
    if (!counts || typeof counts !== "object") return "—";
    const total = counts.total ?? 0;
    return `${total} llamadas (sol. ${counts.solicita_descarga ?? 0} · verif. ${counts.verifica_solicitud ?? 0} · paq. ${counts.descarga_paquete ?? 0})`;
  };

  const loadPeriodResumen = async (year, month) => {
    if (!clientId) return;
    setPeriodBusy(true);
    setPeriodResumen(null);
    try {
      const { data } = await api.get("/sat/resumen-periodo", {
        params: { client_id: clientId, year, month },
      });
      setPeriodResumen(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar el resumen del periodo");
    } finally {
      setPeriodBusy(false);
    }
  };

  const onSelectPeriod = async (year, month) => {
    const same = selectedPeriod?.year === year && selectedPeriod?.month === month;
    if (same) {
      setSelectedPeriod(null);
      setPeriodResumen(null);
      return;
    }
    setSelectedPeriod({ year, month });
    const range = monthIsoRange(year, month);
    if (range.start && range.end) {
      setAutoDlStart(range.start);
      setAutoDlEnd(range.end);
      toast.info(
        `Periodo de descarga: ${formatIsoDateMx(range.start)} — ${formatIsoDateMx(range.end)}`,
        { duration: 4000 },
      );
    }
    await loadPeriodResumen(year, month);
  };

  const onExportPeriodZip = async () => {
    if (!clientId || !selectedPeriod) return;
    setExportBusy(true);
    try {
      const name = await downloadZipBlob(
        "/sat/exportar-periodo-zip",
        { client_id: clientId, year: selectedPeriod.year, month: selectedPeriod.month },
        `CFDI_SAT_${selectedPeriod.year}-${String(selectedPeriod.month).padStart(2, "0")}.zip`,
      );
      toast.success(`Exportación lista: ${name}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "No se pudo exportar el ZIP");
    } finally {
      setExportBusy(false);
    }
  };

  const renderResumenFlujo = (titulo, bloque, tipos) => {
    if (!bloque) return null;
    const rows = tipos.map((key) => ({ key, label: RESUMEN_TIPOS[key], ...(bloque[key] || {}) })).filter((r) => r.total > 0);
    if (!rows.length) return null;
    return (
      <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
        <div className="text-[10px] font-bold uppercase text-slate-500">{titulo}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {rows.map((r) => (
            <div key={r.key} className="text-[10px] rounded border border-slate-100 bg-slate-50/80 px-2.5 py-2">
              <div className="font-semibold text-slate-800">{r.label}</div>
              <div className="tabular-nums text-slate-700">Total: <strong>{r.total ?? 0}</strong></div>
              <div className="text-slate-500">Último: {formatTsMx(r.ultimo_timbrado)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderResumenEjecutivo = (resumen) => {
    if (!resumen) return null;
    return (
      <div className="space-y-3 mt-3">
        {renderResumenFlujo("Emitidos", resumen.emitido, ["ingresos", "egresos", "nominas", "pagos", "traslados"])}
        {renderResumenFlujo("Recibidos", resumen.recibido, ["egresos", "pagos", "traslados"])}
      </div>
    );
  };

  const attachedActiveJobRef = useRef(null);

  useEffect(() => {
    const jobId = activeSatJob?.job_id;
    if (!jobId || attachedActiveJobRef.current === jobId) return;
    attachedActiveJobRef.current = jobId;
    attachToSatJob(jobId, activeSatJob);
  }, [activeSatJob, attachToSatJob]);

  useEffect(() => {
    if (!clientId || !hasActiveSatPipeline) return undefined;
    const timer = setInterval(() => {
      loadPreflight();
      loadProcesoBitacora();
    }, 20000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, hasActiveSatPipeline]);

  useEffect(() => {
    if (!clientId) {
      setSecureFielStatus(null);
      setMatriz(null);
      setMatrizError(null);
      setSelectedPeriod(null);
      setPeriodResumen(null);
      setDescargasHistory([]);
      setProcesoBitacora(null);
      setAutoDlResult(null);
      setPreflight(null);
      attachedActiveJobRef.current = null;
      return;
    }
    reloadSecureFielStatus();
    loadPreflight();
    loadMatriz();
    loadDescargasHistory();
    loadProcesoBitacora();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const onAutoDownloadSat = async () => {
    if (!clientId) {
      toast.error("Selecciona un cliente");
      return;
    }
    if (!secureFielStatus?.configured) {
      toast.error("Configure la e.firma en Clientes → Certificados y FIEL");
      return;
    }
    if (preflight?.active_job?.job_id) {
      attachToSatJob(preflight.active_job.job_id, preflight.active_job);
      toast.info("Ya hay una descarga en curso; mostrando el avance actual (sin duplicar solicitud al SAT).");
      return;
    }
    if (preflight?.can_coalesce_to_active && preflight?.download_coalesce?.recent_sat_requests) {
      showVirtualSatProgress(
        {
          step: "verify",
          message:
            "El SAT está procesando la solicitud anterior. Espere; no se enviará otra solicitud.",
          percent: 15,
        },
        "Solicitud SAT reciente en proceso. El sistema no duplicará la descarga.",
      );
      return;
    }
    if (preflight && !preflight.can_start_download) {
      const msg = preflight.blockers?.[0]?.message || "Pre-flight bloqueó la descarga. Revise Configuración → Gestión de llaves.";
      toast.error(msg);
      return;
    }
    if (!autoDlStart || !autoDlEnd) {
      toast.error("Seleccione fecha inicio y fecha fin");
      return;
    }
    const todayMx = todayIsoMx();
    let fechaInicio = autoDlStart;
    let fechaFin = autoDlEnd;
    if (fechaFin > todayMx) {
      toast.warning(`La fecha fin se ajustó a hoy (${formatIsoDateMx(todayMx)}). El SAT no acepta fechas futuras.`);
      fechaFin = todayMx;
      setAutoDlEnd(todayMx);
    }
    if (fechaInicio > todayMx) {
      toast.error("La fecha inicio no puede ser posterior a hoy (hora México).");
      return;
    }
    if (fechaFin < fechaInicio) {
      toast.error("La fecha fin debe ser igual o posterior a la fecha inicio.");
      return;
    }
    setAutoDlBusy(true);
    setAutoDlResult(null);
    setDlProgress({ step: "queued", message: "Iniciando descarga…", percent: 0 });
    const fd = new FormData();
    fd.append("client_id", clientId);
    fd.append("fecha_inicio", fechaInicio);
    fd.append("fecha_fin", fechaFin);
    try {
      const { data: startData } = await api.post("/sat/descarga-automatica/iniciar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
        validateStatus: (s) => s === 202 || (s >= 200 && s < 300),
      });
      if (startData.warning) {
        toast.info(startData.warning);
        if (startData.period?.fecha_fin) {
          setAutoDlEnd(startData.period.fecha_fin);
        }
        if (startData.period?.fecha_inicio) {
          setAutoDlStart(startData.period.fecha_inicio);
        }
      }
      if (startData.coalesced) {
        if (startData.job_id) {
          attachToSatJob(startData.job_id, startData.active_job || { fecha_inicio: fechaInicio, fecha_fin: fechaFin });
        } else if (startData.virtual_progress) {
          showVirtualSatProgress(startData.virtual_progress, startData.message);
        }
        toast.info(startData.message || "Descarga SAT en proceso (sin solicitud duplicada al SAT).");
      } else {
        const jobId = startData.job_id;
        if (!jobId) throw new Error("No se recibió job_id");
        attachToSatJob(jobId, { fecha_inicio: fechaInicio, fecha_fin: fechaFin });
        toast.info("Descarga en segundo plano. Puede cambiar de pantalla; el avance sigue en la barra superior.");
      }
      setDlProgress({ step: "running", message: "Descarga en segundo plano…", percent: 5 });
    } catch (e) {
      const failure = resolveSatDownloadFailure(e);
      setDlProgress({
        step: "error",
        message: failure.technicalSummary || failure.detail || failure.message || "Error al iniciar la descarga",
        percent: 100,
        satCode: failure.code,
      });
      if (failure.showSatAlert) {
        setSatConnectionNotice({
          title: failure.title,
          message: failure.message,
          detail: failure.detail,
        });
      } else {
        toast.error(failure.detail || "Error al iniciar la descarga");
      }
    } finally {
      setAutoDlBusy(false);
    }
  };

  const satToday = todayIsoMx();
  const satMaxStart = autoDlEnd && autoDlEnd < satToday ? autoDlEnd : satToday;

  const credentialsLink = `/clientes?tab=credenciales${clientId ? `&client=${encodeURIComponent(clientId)}` : ""}`;
  const syncPanelLink = `/clientes?tab=sincronizacion${clientId ? `&client=${encodeURIComponent(clientId)}` : ""}`;

  return (
    <>
    <SatDownloadPanelStack>
      <SatDownloadPanelIntro
        icon={CloudDownload}
        iconWrapClassName="bg-blue-50 border-blue-100 text-blue-600"
        title="Descarga manual por e.Firma"
        description={
          <>
            Descargue CFDI por rango de fechas con la e.firma del cliente. Requiere{" "}
            <strong>sincronización automática activa</strong> en{" "}
            <Link to={syncPanelLink} className="underline font-medium text-inherit">
              Clientes → Sincronización SAT
            </Link>
            . Certificados en <strong>Clientes → Certificados y FIEL</strong>.
          </>
        }
      />

      <SatDownloadSectionCard title="Estado de credenciales FIEL">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <p className="text-[10px] text-slate-600 m-0 leading-relaxed">
            Consulta programática del perfil centralizado. Para cargar o renovar certificados use Clientes.
          </p>
          <Button type="button" variant="outline" size="sm" className="h-8 text-[10px]" asChild>
            <Link to={credentialsLink}>
              <ExternalLink className="w-3 h-3 mr-1.5" />
              Gestionar credenciales
            </Link>
          </Button>
        </div>
        {secureFielLoading ? (
          <div className="flex items-center gap-2 text-[10px] text-slate-500">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Consultando estado…
          </div>
        ) : secureFielStatus?.configured ? (
          <div className="text-[10px] rounded-md border border-emerald-200 bg-emerald-50/80 text-emerald-950 p-2.5">
            <ShieldCheck className="w-3.5 h-3.5 inline mr-1" />
            e.firma configurada · RFC certificado: {secureFielStatus.rfc_certificado || "—"}
            {secureFielStatus.fiel_expiry ? (
              <span className="ml-2">· vigencia hasta {secureFielStatus.fiel_expiry.slice(0, 10)}</span>
            ) : null}
            {secureFielStatus.traffic_light ? (
              <span className="ml-2 uppercase font-semibold">· semáforo: {secureFielStatus.traffic_light}</span>
            ) : null}
          </div>
        ) : (
          <div className="text-[10px] rounded-md border border-amber-200 bg-amber-50/80 text-amber-950 p-2.5">
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
            Sin e.firma para este cliente. Configure certificados en Clientes antes de descargar.
          </div>
        )}
        <Button type="button" variant="ghost" size="sm" className="h-7 text-[10px] px-2 w-fit" onClick={reloadSecureFielStatus} disabled={!clientId || secureFielLoading}>
          {secureFielLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
          Actualizar estado
        </Button>
      </SatDownloadSectionCard>

      {preflight && !preflight.ready ? (
        <SatDownloadSectionCard title="Pre-flight" className="border-rose-200 bg-rose-50/30">
          <p className="text-[11px] font-bold text-rose-900 flex items-center gap-1.5 m-0">
            <AlertTriangle className="w-3.5 h-3.5" /> Descarga bloqueada
          </p>
          <ul className="text-[10px] text-rose-950/90 list-disc pl-4 space-y-1 m-0">
            {(preflight.blockers || []).map((b, i) => (
              <li key={`${b.code}-${i}`}>{b.message}</li>
            ))}
          </ul>
          <p className="text-[9px] text-rose-800 m-0">
            Revise credenciales en Clientes o <strong>Configuración → Sistema → Gestión de llaves</strong>.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" className="h-7 text-[10px] w-fit" onClick={loadPreflight} disabled={preflightBusy}>
              {preflightBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Revalidar pre-flight"}
            </Button>
            {downloadInProgressBlocked ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-7 text-[10px] w-fit border-amber-300 bg-amber-50 text-amber-950 hover:bg-amber-100"
                onClick={onReleaseStuckDownload}
                disabled={releaseSlotBusy || !clientId}
              >
                {releaseSlotBusy ? (
                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="w-3 h-3 mr-1" />
                )}
                Liberar descarga trabada
              </Button>
            ) : null}
          </div>
          {downloadInProgressBlocked ? (
            <p className="text-[9px] text-rose-800/90 m-0 leading-relaxed">
              Use esto si se cortó Internet o cerró el navegador durante una descarga. El sistema también libera
              jobs inactivos tras unos minutos automáticamente.
            </p>
          ) : null}
        </SatDownloadSectionCard>
      ) : preflightBusy ? (
        <p className="text-[10px] text-slate-500 flex items-center gap-1 m-0 px-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Verificando pre-flight…
        </p>
      ) : preflight?.ready ? (
        <div className="space-y-2 px-1">
          {!syncAutoActive ? (
            <div className="text-[10px] rounded-md border border-amber-300 bg-amber-50 text-amber-950 p-2.5 m-0 space-y-2">
              <p className="m-0">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                Active la <strong>sincronización automática SAT</strong> y defina la fecha de inicio antes de
                iniciar una descarga manual.
              </p>
              <Button type="button" variant="outline" size="sm" className="h-8 text-[10px] w-fit" asChild>
                <Link to={syncPanelLink} data-testid="sat-fiel-goto-sync-setup">
                  Ir a Sincronización SAT
                </Link>
              </Button>
            </div>
          ) : null}
          {satCoolingOff ? (
            <p className="text-[10px] rounded-md border border-rose-200 bg-rose-50 text-rose-900 p-2 m-0">
              <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
              Enfriamiento SAT activo
              {preflight.sat_cooling_off_until
                ? ` hasta ${formatTsMx(preflight.sat_cooling_off_until)}.`
                : "."}{" "}
              {statusLine?.next_retry_label
                ? `Reintento estimado ~${statusLine.next_retry_label} (hora México). `
                : ""}
              El botón permanece deshabilitado hasta entonces; la sincronización automática reintentará sola.
            </p>
          ) : null}
          {diagnoseEnabled && statusLine?.label && statusLine.phase !== "idle" ? (
            <p className="text-[10px] rounded-md border border-blue-200 bg-blue-50/90 text-blue-950 p-2 m-0 font-medium tabular-nums">
              <Loader2 className="w-3.5 h-3.5 inline mr-1 animate-spin" />
              Estado SAT: {statusLine.label}
            </p>
          ) : null}
          {diagnoseEnabled && globalAtCapacity ? (
            <p className="text-[10px] rounded-md border border-amber-200 bg-amber-50 text-amber-950 p-2 m-0">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              Muchas descargas SAT en la plataforma (
              {preflight.global_sat_capacity?.active}/{preflight.global_sat_capacity?.max_concurrent} en curso). Su
              solicitud entrará en <strong>cola global</strong> hasta que haya cupo.
            </p>
          ) : null}
        </div>
      ) : null}

      <SatDownloadSectionCard title="Periodo de descarga">
        <p className="text-[10px] text-slate-600 m-0 leading-relaxed">
          Seleccione el periodo en calendario (hora México). El backend envía al SAT{" "}
          <span className="font-mono text-[9px]">AAAA-MM-DDThh:mm:ss</span>; no se permiten fechas futuras.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Fecha inicio</Label>
            <DateFieldMx
              value={autoDlStart}
              onChange={setAutoDlStart}
              className="h-9 mt-1 text-xs bg-white"
              disabled={panelBusy || !clientId}
              maxIso={satMaxStart}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Fecha fin</Label>
            <DateFieldMx
              value={autoDlEnd}
              onChange={setAutoDlEnd}
              className="h-9 mt-1 text-xs bg-white"
              disabled={panelBusy || !clientId}
              minIso={autoDlStart || undefined}
              maxIso={satToday}
            />
          </div>
        </div>
        <p className="text-[10px] text-slate-500">
          Calendario dd/mm/aaaa (desplegables de mes y año) · máximo hasta hoy ({formatIsoDateMx(todayIsoMx())}) ·
          máximo <strong>{MANUAL_MAX_PERIOD_DAYS} días</strong> por descarga manual · bloques internos de 30 días con
          pausas anti-cuota SAT.
        </p>
        {manualPeriodTooLong ? (
          <div className="text-[10px] rounded-md border border-amber-300 bg-amber-50 text-amber-950 p-2 m-0 space-y-2">
            <p className="m-0">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              El rango seleccionado ({manualPeriodDays} días) supera el máximo manual ({MANUAL_MAX_PERIOD_DAYS} días).
              Para periodos largos use la <strong>sincronización automática SAT</strong> (el robot continúa en segundo plano).
            </p>
            {!syncAutoActive ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-[10px] border-amber-400 text-amber-950 hover:bg-amber-100"
                disabled={!clientId}
                asChild
              >
                <Link to={syncPanelLink}>Configurar sincronización SAT</Link>
              </Button>
            ) : (
              <p className="m-0 text-emerald-800 font-semibold">Sincronización automática ya está activa para este cliente.</p>
            )}
          </div>
        ) : suggestAutoSync ? (
          <div className="text-[10px] rounded-md border border-sky-200 bg-sky-50 text-sky-950 p-2 m-0 space-y-2">
            <p className="m-0">
              Rango amplio ({manualPeriodDays} días). Para evitar repetir descargas manuales, puede activar la
              sincronización automática; el sistema procesará los tramos sin saturar al SAT.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-[10px] border-sky-300 text-sky-950 hover:bg-sky-100"
              disabled={!clientId}
              asChild
            >
              <Link to={syncPanelLink}>Configurar sincronización SAT (recomendado)</Link>
            </Button>
          </div>
        ) : null}
        <Button
          type="button"
          onClick={onAutoDownloadSat}
          disabled={
            autoDlBusy ||
            panelBusy ||
            !clientId ||
            !secureFielStatus?.configured ||
            manualPeriodTooLong ||
            satCoolingOff ||
            !syncAutoActive
          }
          className="w-full bg-blue-600 hover:bg-blue-700 h-10 font-bold"
        >
          {autoDlBusy ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {dlProgress?.message || "Procesando descarga SAT…"}
            </>
          ) : satCoolingOff ? (
            <>
              <AlertCircle className="w-4 h-4 mr-2" />
              Disponible{" "}
              {statusLine?.next_retry_label
                ? `~${statusLine.next_retry_label}`
                : preflight?.sat_cooling_off_until
                  ? formatTsMx(preflight.sat_cooling_off_until)
                  : "más tarde"}
            </>
          ) : hasActiveSatPipeline ? (
            <>
              <Loader2 className="w-4 h-4 mr-2" />
              Ver descarga en proceso
            </>
          ) : (
            <>
              <CloudDownload className="w-4 h-4 mr-2" />
              Iniciar Descarga de CFDI
            </>
          )}
        </Button>
        {hasActiveSatPipeline && !autoDlBusy ? (
          <p className="text-[10px] rounded-md border border-blue-200 bg-blue-50 text-blue-900 p-2 m-0">
            Hay una descarga SAT activa o en cola para este cliente. Si pulsa el botón solo verá el avance;
            <strong> no se enviará otra solicitud al SAT</strong>.
          </p>
        ) : null}
        {(autoDlBusy || hasActiveSatPipeline || dlProgress?.step === "error") && dlProgress ? (
          <div
            className={`rounded-md border p-3 space-y-2 ${
              dlProgress.step === "error"
                ? "border-red-200 bg-red-50/80"
                : "border-blue-200 bg-white"
            }`}
          >
            <div
              className={`flex items-center gap-2 text-[10px] ${
                dlProgress.step === "error" ? "text-red-900" : "text-slate-600"
              }`}
            >
              {dlProgress.step === "error" ? (
                <AlertCircle className="h-3.5 w-3.5 shrink-0 text-red-600" />
              ) : (
                <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
              )}
              <span className="font-semibold uppercase">{dlProgress.step}</span>
              {dlProgress.step !== "error" ? (
                <span className="ml-auto tabular-nums">{dlProgress.percent ?? 0}%</span>
              ) : null}
            </div>
            {dlProgress.step !== "error" ? (
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all duration-500"
                  style={{ width: `${Math.min(100, Math.max(0, dlProgress.percent ?? 0))}%` }}
                />
              </div>
            ) : null}
            <p className={`text-[10px] m-0 ${dlProgress.step === "error" ? "text-red-950" : "text-slate-600"}`}>
              {dlProgress.message}
            </p>
            {dlProgress.step === "verify" && dlProgress.maxAttempts > 0 ? (
              <p className="text-[10px] m-0 text-slate-500">
                El SAT puede tardar varios minutos en preparar los paquetes
                {dlProgress.direccion ? ` (${dlProgress.direccion})` : ""}.
                {dlProgress.attempt > 0 ? ` Intento ${dlProgress.attempt}/${dlProgress.maxAttempts}.` : ""}
              </p>
            ) : null}
            {dlProgress.step === "error" && dlProgress.batchPeriod ? (
              <p className="text-[9px] m-0 text-red-800/90">
                Bloque: {dlProgress.batchPeriod}
              </p>
            ) : null}
            {dlProgress.step === "error" && dlProgress.satMensaje && dlProgress.satMensaje !== dlProgress.message ? (
              <p className="text-[9px] m-0 text-red-900 font-semibold break-words">
                Mensaje SAT: {dlProgress.satMensaje}
              </p>
            ) : null}
            {dlProgress.step === "error" && dlProgress.satHint && dlProgress.satHint !== dlProgress.message ? (
              <p className="text-[9px] m-0 text-red-800/80 font-mono break-words">
                {dlProgress.satHint}
              </p>
            ) : null}
            {dlProgress.step === "error" && dlProgress.satCode === "SAT_DOWNLOAD_IN_PROGRESS" ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-[10px] mt-1 border-amber-300"
                onClick={onReleaseStuckDownload}
                disabled={releaseSlotBusy}
              >
                {releaseSlotBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Liberar descarga trabada
              </Button>
            ) : null}
          </div>
        ) : null}
        {!secureFielStatus?.configured && clientId && (
          <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            Configure la e.firma en{" "}
            <Link to={credentialsLink} className="underline font-medium">Clientes → Certificados y FIEL</Link>
            {" "}antes de descargar.
          </p>
        )}
        {secureFielStatus?.configured && secureFielStatus?.certificate_kind === "unknown" && !autoDlResult?.ok && (
          <p className="text-[10px] text-slate-700 bg-slate-50 border border-slate-200 rounded p-2">
            e.firma guardada. El sistema no pudo clasificar el .cer automáticamente (FIEL vs CSD).
            Si la descarga SAT funciona, su certificado es válido. Si recibe error 301, vuelva a cargar la
            e.firma en{" "}
            <Link to={credentialsLink} className="underline font-medium">Clientes → Certificados y FIEL</Link>.
          </p>
        )}
        {secureFielStatus?.configured && secureFielStatus?.certificate_kind === "fiel" && (
          <p className="text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
            Certificado detectado: <strong>e.firma (FIEL)</strong>
            {secureFielStatus.rfc_certificado ? ` · RFC ${secureFielStatus.rfc_certificado}` : ""}
          </p>
        )}
        {secureFielStatus?.configured && secureFielStatus?.certificate_kind === "csd" && (
          <p className="text-[10px] text-rose-900 bg-rose-50 border border-rose-200 rounded p-2">
            El certificado guardado es un <strong>CSD</strong> (sello digital), no una e.firma (FIEL).
            La descarga masiva del SAT requiere la e.firma. Vuelva a cargar el .cer correcto en{" "}
            <Link to={credentialsLink} className="underline font-medium">Clientes → Certificados y FIEL</Link>.
          </p>
        )}
        {autoDlResult?.ok && (
          <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 text-emerald-950" role="status">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600 mt-0.5" />
              <div className="text-sm">
                <div className="font-bold">Descarga procesada</div>
                <p className="text-[12px] mt-1">
                  Periodo {formatIsoDateMx(autoDlResult.periodo_inicio)} al {formatIsoDateMx(autoDlResult.periodo_fin)}.
                  {" "}Se procesaron/actualizaron {autoDlResult.total_cfdis ?? 0} comprobantes.
                </p>
                {renderResumenEjecutivo(autoDlResult.resumen_ejecutivo)}
              </div>
            </div>
          </div>
        )}
      </SatDownloadSectionCard>

      <SatDownloadSectionCard title="Matriz histórica por año y mes">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] text-slate-500 m-0 leading-relaxed flex-1">
            Resumen de CFDIs almacenados. Seleccione un mes para ver estadísticas, exportar XML
            y rellenar las fechas del panel de descarga.
          </p>
          <Button type="button" variant="ghost" size="sm" className="h-7 text-[10px] shrink-0" disabled={!clientId || panelBusy || matrizBusy} onClick={loadMatriz}>
            {matrizBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Actualizar
          </Button>
        </div>
        {matrizError && (
          <div className="rounded-md border border-red-200 bg-red-50/90 px-3 py-2 text-[10px] text-red-800">{matrizError}</div>
        )}
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full text-[10px] min-w-[720px]">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left font-semibold px-3 py-2 w-16">Año</th>
                {MESES_CORTO.map((m) => (
                  <th key={m} className="text-center font-semibold px-1 py-2">{m}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(matriz?.years || []).map((yrow) => {
                const monthMap = Object.fromEntries((yrow.months || []).map((m) => [m.month, m]));
                return (
                  <tr key={yrow.year} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-bold text-[#0F2D5C] tabular-nums">{yrow.year}</td>
                    {MESES_CORTO.map((_, idx) => {
                      const m = idx + 1;
                      const cell = monthMap[m];
                      const active = selectedPeriod?.year === yrow.year && selectedPeriod?.month === m;
                      const total = cell?.total ?? 0;
                      return (
                        <td key={m} className="px-1 py-1 text-center">
                          <button
                            type="button"
                            disabled={!clientId || panelBusy}
                            onClick={() => onSelectPeriod(yrow.year, m)}
                            className={`w-full min-w-[44px] rounded px-1 py-1.5 tabular-nums transition-colors ${
                              active
                                ? "bg-blue-600 text-white font-bold ring-2 ring-blue-300"
                                : total > 0
                                  ? "bg-emerald-50 text-emerald-900 border border-emerald-200 hover:bg-emerald-100 font-semibold"
                                  : "bg-slate-50 text-slate-400 border border-slate-100 hover:bg-slate-100"
                            }`}
                            title={total > 0 ? `${total} CFDI · último ${formatTsMx(cell?.ultimo_timbrado)}` : "Sin datos"}
                          >
                            {total > 0 ? total : "·"}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {!matrizBusy && (!matriz?.years || matriz.years.length === 0) && (
                <tr>
                  <td colSpan={13} className="px-3 py-10 text-center text-slate-400">
                    {clientId ? "Sin CFDIs clasificados por periodo. Ejecute una descarga o pulse Actualizar." : "Seleccione un cliente."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {selectedPeriod && (
          <div className="rounded-md border border-blue-200 bg-blue-50/40 p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-[11px] font-bold text-[#0F2D5C]">
                Resumen — {MESES_CORTO[selectedPeriod.month - 1]} {selectedPeriod.year}
              </div>
              <Button
                type="button"
                size="sm"
                className="h-8 text-[10px] bg-[#0F2D5C] hover:bg-[#0a2247]"
                disabled={exportBusy || periodBusy || !periodResumen?.total_cfdis}
                onClick={onExportPeriodZip}
              >
                {exportBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                Exportar XML de este periodo a PC
              </Button>
            </div>
            {periodBusy && (
              <div className="flex items-center gap-2 text-[10px] text-slate-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando resumen…
              </div>
            )}
            {periodResumen && !periodBusy && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
                  <div className="rounded border bg-white px-2.5 py-2"><span className="text-slate-500">Total CFDIs</span><div className="font-bold tabular-nums text-lg">{periodResumen.total_cfdis}</div></div>
                  <div className="rounded border bg-white px-2.5 py-2"><span className="text-slate-500">Cancelados</span><div className="font-bold tabular-nums text-lg">{periodResumen.cancelados ?? 0}</div></div>
                  <div className="rounded border bg-white px-2.5 py-2 col-span-2"><span className="text-slate-500">Último timbrado del mes</span><div className="font-semibold">{formatTsMx(periodResumen.ultimo_timbrado)}</div></div>
                </div>
                {renderResumenFlujo("Emitidos", periodResumen.emitido, ["ingresos", "egresos", "nominas", "pagos", "traslados"])}
                {renderResumenFlujo("Recibidos", periodResumen.recibido, ["egresos", "pagos", "traslados"])}
              </div>
            )}
          </div>
        )}
      </SatDownloadSectionCard>

      {diagnoseEnabled &&
        (procesoBitacora?.active?.length > 0 ||
          procesoBitacora?.diagnostics?.length > 0 ||
          procesoBitacora?.summaries?.length > 0) && (
        <SatDownloadSectionCard title="Bitácora de llamadas al SAT">
          <p className="text-[10px] text-slate-600 m-0 mb-2 leading-relaxed">
            Registro interno de intentos SOAP. Al completar un periodo con éxito se conserva solo el último
            resumen; las fallas por cupo (5002) permanecen hasta resolverse.
          </p>
          {procesoBitacora?.active?.map((row) => (
            <div
              key={row.id}
              className="rounded-md border border-blue-200 bg-blue-50/80 px-2.5 py-2 text-[10px] mb-2"
            >
              <div className="font-semibold text-blue-900">En curso</div>
              <div className="text-slate-700">
                {formatIsoDateMx(row.periodo_inicio)} — {formatIsoDateMx(row.periodo_fin)}
              </div>
              <div className="text-slate-600">{formatCallCounts(row.call_counts)}</div>
            </div>
          ))}
          {procesoBitacora?.diagnostics?.slice(0, 3).map((row) => (
            <div
              key={row.id}
              className="rounded-md border border-amber-200 bg-amber-50/80 px-2.5 py-2 text-[10px] mb-2"
            >
              <div className="font-semibold text-amber-950">
                {row.status === "rate_limited" ? "Cupo SAT / en espera" : "Parcial o fallo"}
              </div>
              <div className="text-slate-700">
                {formatIsoDateMx(row.periodo_inicio)} — {formatIsoDateMx(row.periodo_fin)}
              </div>
              <div className="text-slate-600">{formatCallCounts(row.call_counts)}</div>
              {row.outcome_note ? <div className="text-slate-500 mt-1">{row.outcome_note}</div> : null}
            </div>
          ))}
          {procesoBitacora?.summaries?.slice(0, 2).map((row) => (
            <div
              key={row.id}
              className="rounded-md border border-emerald-200 bg-emerald-50/60 px-2.5 py-2 text-[10px] mb-2"
            >
              <div className="font-semibold text-emerald-900">Último éxito</div>
              <div className="text-slate-700">
                {formatIsoDateMx(row.periodo_inicio)} — {formatIsoDateMx(row.periodo_fin)}
              </div>
              <div className="text-slate-600">
                {formatCallCounts(row.call_counts)} · {row.total_cfdis ?? 0} CFDIs
              </div>
              {row.closed_at ? (
                <div className="text-slate-500">{formatTsMx(row.closed_at)}</div>
              ) : null}
            </div>
          ))}
        </SatDownloadSectionCard>
      )}

      {diagnoseEnabled && descargasHistory.length > 0 && (
        <SatDownloadSectionCard title="Últimas descargas automáticas">
          <ul className="divide-y divide-slate-200 text-[10px] m-0 p-0 list-none">
            {descargasHistory.slice(0, 5).map((row) => (
              <li key={row.id || row.fecha_ejecucion} className="py-1.5 flex flex-wrap justify-between gap-2">
                <span className="font-mono text-slate-600">{formatIsoDateMx(row.periodo_inicio)} — {formatIsoDateMx(row.periodo_fin)}</span>
                <span className="tabular-nums font-semibold">{row.total_cfdis ?? 0} CFDIs</span>
                <span className="text-slate-500">{row.fecha_ejecucion ? formatTsMx(row.fecha_ejecucion) : "—"}</span>
              </li>
            ))}
          </ul>
        </SatDownloadSectionCard>
      )}
    </SatDownloadPanelStack>
    <AlertDialog open={!!satConnectionNotice} onOpenChange={(open) => !open && setSatConnectionNotice(null)}>
      <AlertDialogContent className="max-w-md rounded-sm">
        <AlertDialogHeader>
          <AlertDialogTitle>{satConnectionNotice?.title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-slate-600">
              <p>{satConnectionNotice?.message}</p>
              {satConnectionNotice?.detail ? (
                <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  {satConnectionNotice.detail}
                </p>
              ) : null}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction className="bg-[#0F2D5C]" onClick={() => setSatConnectionNotice(null)}>
            Aceptar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
